import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  BellRing, PlayCircle, RefreshCw, Users, CalendarDays, CircleDollarSign,
  Save as SaveIcon, History,
} from "lucide-react";

/**
 * Delinquency Nudges — configure automatic weekly ledger statement emails
 * for residents whose balance stays past due.  Includes a live "candidates"
 * preview and manual "run now" button so admins can dry-run + demo it.
 */
export function DelinquencyNudgesCard() {
  const [cfg, setCfg] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    try {
      const [cRes, candRes, logRes] = await Promise.all([
        api.get("/admin/delinquency-nudges/config"),
        api.get("/admin/delinquency-nudges/candidates"),
        api.get("/admin/delinquency-nudges/log"),
      ]);
      setCfg(cRes.data?.config || null);
      setCandidates(candRes.data?.candidates || []);
      setLog(logRes.data?.items || []);
    } catch (e) {
      // Silently ignore 403s — this card is admin-only, and it briefly
      // renders on other roles' /admin/settings before RequireAuth redirects.
      const status = e?.response?.status;
      if (status !== 401 && status !== 403) {
        toast.error(apiErrorMessage(e));
      }
    }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/delinquency-nudges/config", cfg);
      toast.success("Delinquency nudge settings saved");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const runNow = async () => {
    if (candidates.length === 0) {
      toast.info("No residents meet the criteria right now.");
      return;
    }
    if (!window.confirm(
      `Send a nudge email to ${candidates.length} resident${candidates.length === 1 ? "" : "s"} right now?`,
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/delinquency-nudges/run-now");
      const bits = [];
      if (data.sent) bits.push(`${data.sent} delivered`);
      if (data.queued) bits.push(`${data.queued} queued`);
      if (data.failed) bits.push(`${data.failed} failed`);
      toast.success(`Nudged ${data.attempted} residents (${bits.join(", ") || "done"})`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setRunning(false);
  };

  if (busy) {
    return (
      <Card className="border-white/10 bg-card p-5" data-testid="delinquency-nudges">
        <div className="text-sm text-zinc-400">Loading delinquency-nudge settings…</div>
      </Card>
    );
  }

  // Short-circuit if the config never loaded (e.g. 401/403 for non-admin
  // roles that briefly render /admin/settings before RequireAuth redirects).
  if (!cfg) return null;

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="delinquency-nudges">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1">
            <BellRing className="h-3 w-3" /> Delinquency Nudges
          </div>
          <div className="text-lg font-semibold font-display mt-1">
            Auto-email statements to past-due residents
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-400">Enabled</span>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="delinq-enabled"
          />
          {cfg.enabled ? (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
              Active
            </Badge>
          ) : (
            <Badge variant="outline" className="border-white/10 text-zinc-400">
              Paused
            </Badge>
          )}
        </div>
      </div>
      <div className="text-sm text-zinc-400 mt-2">
        Every 4 hours we check every resident&apos;s ledger.  Anyone who&apos;s more than
        <b> ${Number(cfg.min_balance_past_due).toFixed(2)}</b> past due for
        <b> {cfg.min_days_past_due}+ days</b> gets a fresh statement PDF and a friendly note.
        Each resident is only nudged once every <b>{cfg.cadence_days} days</b>.
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs flex items-center gap-1"><CircleDollarSign className="h-3 w-3" /> Min balance past due ($)</Label>
          <Input
            type="number" min="0" step="1"
            value={cfg.min_balance_past_due ?? 0}
            onChange={(e) => setCfg({ ...cfg, min_balance_past_due: Number(e.target.value) })}
            data-testid="delinq-min-balance"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Min days past due</Label>
          <Input
            type="number" min="0" step="1"
            value={cfg.min_days_past_due ?? 0}
            onChange={(e) => setCfg({ ...cfg, min_days_past_due: Number(e.target.value) })}
            data-testid="delinq-min-dpd"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Cadence (days)</Label>
          <Input
            type="number" min="1" max="90" step="1"
            value={cfg.cadence_days ?? 7}
            onChange={(e) => setCfg({ ...cfg, cadence_days: Number(e.target.value) })}
            data-testid="delinq-cadence"
          />
        </div>
      </div>

      <div className="mt-3">
        <Label className="text-xs">Note that appears in the nudge email</Label>
        <Textarea
          rows={2}
          value={cfg.note_template || ""}
          onChange={(e) => setCfg({ ...cfg, note_template: e.target.value })}
          data-testid="delinq-note"
        />
      </div>

      {/* Channel selector — Email + optional SMS via GHL */}
      <div className="mt-4 rounded-md border border-white/10 p-3 space-y-3" data-testid="delinq-channels">
        <div className="overline">Delivery channels</div>
        <label className="flex items-start gap-3 text-sm text-zinc-200 select-none">
          <Switch
            checked={cfg.send_email !== false}
            onCheckedChange={(v) => setCfg({ ...cfg, send_email: v })}
            data-testid="delinq-send-email"
          />
          <div>
            <div>Email with attached statement PDF</div>
            <div className="text-[11px] text-zinc-500">Delivered through Resend. Uses the note above.</div>
          </div>
        </label>
        <label className="flex items-start gap-3 text-sm text-zinc-200 select-none">
          <Switch
            checked={!!cfg.send_sms}
            onCheckedChange={(v) => setCfg({ ...cfg, send_sms: v })}
            data-testid="delinq-send-sms"
          />
          <div>
            <div>SMS reminder via GoHighLevel</div>
            <div className="text-[11px] text-zinc-500">Only sent to residents with SMS consent + a phone on file. Reply threads land in GHL Conversations.</div>
          </div>
        </label>
        {cfg.send_sms && (
          <div>
            <Label className="text-xs">SMS body (placeholders: <code className="text-zinc-300">{"{prop_name}"}</code>, <code className="text-zinc-300">{"{balance:.2f}"}</code>, <code className="text-zinc-300">{"{dpd}"}</code>)</Label>
            <Textarea
              rows={2}
              value={cfg.sms_template || ""}
              onChange={(e) => setCfg({ ...cfg, sms_template: e.target.value })}
              data-testid="delinq-sms-template"
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button data-testid="delinq-save" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          <SaveIcon className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save settings"}
        </Button>
        <Button variant="outline" className="border-white/10" size="sm" onClick={load} disabled={busy}>
          <RefreshCw className="h-3 w-3 mr-1" /> Reload
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-orange-500/40 text-orange-200 bg-orange-500/10 hover:bg-orange-500/20"
          onClick={runNow}
          disabled={running || candidates.length === 0}
          data-testid="delinq-run-now"
        >
          <PlayCircle className="h-3.5 w-3.5 mr-1" /> {running ? "Sending…" : `Run now (${candidates.length})`}
        </Button>
      </div>

      {/* Candidates preview */}
      <div className="mt-5">
        <div className="overline flex items-center gap-1 mb-2">
          <Users className="h-3 w-3" /> Would be nudged on next run ({candidates.length})
        </div>
        {candidates.length === 0 ? (
          <div className="text-xs text-zinc-500 border border-white/5 rounded-md px-3 py-4 text-center" data-testid="delinq-candidates-empty">
            No residents currently meet the criteria — everyone&apos;s in good standing 🎉
          </div>
        ) : (
          <div className="rounded-md border border-white/10 divide-y divide-white/5" data-testid="delinq-candidates">
            {candidates.slice(0, 8).map((c) => (
              <div key={c.resident_id} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`delinq-candidate-${c.resident_id}`}>
                <div>
                  <div className="text-zinc-200">{c.name}</div>
                  <div className="text-[10px] text-zinc-500">{c.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-rose-300">${Number(c.balance).toFixed(2)}</div>
                  <div className="text-[10px] text-zinc-500">
                    {c.days_past_due}d past due
                    {c.last_nudge_at ? ` · last nudge ${new Date(c.last_nudge_at).toLocaleDateString()}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent nudge log */}
      {log.length > 0 && (
        <div className="mt-5">
          <div className="overline flex items-center gap-1 mb-2">
            <History className="h-3 w-3" /> Recent nudges ({log.length})
          </div>
          <div className="rounded-md border border-white/10 divide-y divide-white/5">
            {log.slice(0, 6).map((n) => (
              <div key={n.id} className="px-3 py-2 text-xs flex items-center justify-between">
                <div>
                  <div className="text-zinc-200">${Number(n.balance_at_send).toFixed(2)} · {n.days_past_due}d past due</div>
                  <div className="text-[10px] text-zinc-500">{new Date(n.sent_at).toLocaleString()} · by {n.actor}</div>
                </div>
                <Badge variant="outline" className={
                  n.delivered
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : n.queued
                    ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                    : "border-rose-500/40 text-rose-300 bg-rose-500/10"
                }>
                  {n.delivered ? "delivered" : n.queued ? "queued" : "failed"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
